/** Enhanced cart-checkbox-upsell.js **/

class CartCheckboxUpsell {
  constructor(cartItemsInstance) {
    this.cartItemsInstance = cartItemsInstance;
    this.baseUrl = '/cart';
    this.checkboxUpsellVariantId = this.getUpsellCheckboxVariantId();
    
    // Use global configuration from theme.liquid
    if (typeof window.cartUpsell === 'undefined') {
      console.warn('window.cartUpsell not found. Make sure to add the global configuration in theme.liquid');
      // Fallback configuration
      window.cartUpsell = {
        isEnabled: true,
        variantId: this.checkboxUpsellVariantId,
        autoAdd: true,
        initialized: false
      };
    }
    
    // Sync variant ID from DOM if global config doesn't have it
    if (this.checkboxUpsellVariantId && !window.cartUpsell.variantId) {
      window.cartUpsell.variantId = this.checkboxUpsellVariantId;
    }
    
    this.initEventDelegation();
    this.initPubSubListeners();
    document.addEventListener('cartUpdated', this.handleCartUpdate.bind(this));
    this.updateCheckboxState();
    
    window.cartUpsell.initialized = true;
  }

  initEventDelegation() {
    document.body.addEventListener('change', (event) => {
      if (event.target.id === 'cart-upsell-checkbox') {
        const isChecked = event.target.checked;
        if (isChecked) {
          this.addUpsellCheckboxToCart();
        } else {
          this.removeUpsellCheckboxFromCart();
        }
      }
    });
  }

  initPubSubListeners() {
    // Listen for cart updates from product forms
    document.addEventListener('cart:updated', this.handleProductAdded.bind(this));
    
    // Also listen for the existing cartUpdate event
    document.addEventListener('cartUpdate', this.handleProductAdded.bind(this));
    
    // Listen for cart changes (including removals)
    document.addEventListener('cart:changed', this.handleProductAdded.bind(this));
    
    // Listen for PUB_SUB_EVENTS if available
    if (typeof PUB_SUB_EVENTS !== 'undefined') {
      document.addEventListener(PUB_SUB_EVENTS.cartUpdate, this.handleProductAdded.bind(this));
    }

    // Listen for quantity changes and removals
    document.body.addEventListener('click', (event) => {
      // Check if it's a remove button or quantity change
      if (event.target.closest('.cart-remove-button') || 
          event.target.closest('.quantity__button') ||
          event.target.closest('cart-remove-button')) {
        setTimeout(() => {
          this.handleProductAdded();
        }, 500); // Longer delay for removal actions
      }
    });
  }

  handleProductAdded(event) {
    // Only handle removal logic now, since addition is handled in product-form.js
    if (!window.cartUpsell.isEnabled || !this.checkboxUpsellVariantId) {
      return;
    }

    // Small delay to ensure cart state is updated
    setTimeout(() => {
      this.checkForUpsellRemoval();
    }, 100);
  }

  async checkForUpsellRemoval() {
    try {
      const cart = await this.fetchCart();
      
      // Check if cart has items (excluding the upsell product itself)
      const hasMainProducts = cart.items.some(item => 
        item.variant_id.toString() !== this.checkboxUpsellVariantId
      );
      
      // Check if upsell is already in cart
      const hasUpsell = cart.items.some(item => 
        item.variant_id.toString() === this.checkboxUpsellVariantId
      );

      console.log('Cart check for removal:', { hasMainProducts, hasUpsell, totalItems: cart.items.length });

      // Auto-remove upsell if cart is empty of main products
      if (!hasMainProducts && hasUpsell) {
        console.log('Removing upsell automatically - no main products in cart');
        await this.removeUpsellCheckboxFromCart(false);
        
        // Force a cart update to refresh the UI
        if (this.cartItemsInstance && typeof this.cartItemsInstance.onCartUpdate === 'function') {
          this.cartItemsInstance.onCartUpdate();
        }
        
        this.updateCheckboxState();
      }
    } catch (error) {
      console.error('Error in upsell removal check:', error);
    }
  }

  fetchCart() {
    return fetch(`${this.baseUrl}.js`)
      .then(response => response.json());
  }

  getUpsellCheckboxVariantId() {
    return document.querySelector('#cart-upsell-checkbox')?.getAttribute('data-variant-id');
  }

  updateCheckboxState() {
    this.fetchCart()
      .then(cart => {
        const hasUpsellCheckbox = cart.items.some(item => 
          item.variant_id.toString() === this.checkboxUpsellVariantId
        );
        const checkboxUpsellCheckbox = document.querySelector('#cart-upsell-checkbox');
        if (checkboxUpsellCheckbox) {
          checkboxUpsellCheckbox.checked = hasUpsellCheckbox;
        }
      })
      .catch(error => {
        console.log('Error fetching cart:', error);
      });
  }

  handleCartUpdate() {
    setTimeout(() => {
      this.updateCheckboxState();
      
      // Check for upsell removal (addition is now handled in product-form.js)
      this.checkForUpsellRemoval();
    }, 100); 
  }

  addUpsellCheckboxToCart(updateCart = true) {
    const formData = new FormData();
    formData.append('id', this.checkboxUpsellVariantId);
    formData.append('quantity', 1);

    return fetch(`${this.baseUrl}/add.js`, {
      method: 'POST',
      body: formData
    })
    .then(response => {
      if (response.ok && updateCart) {
        // Force full cart update to refresh all sections including checkout button
        this.onCartUpdateCheckboxUpsell();
      }
      return response.json();
    })
    .catch(error => {
      console.error('Error adding cart upsell:', error);
    });
  }

  removeUpsellCheckboxFromCart(updateCart = true) {
    return this.fetchCart()
      .then(cart => {
        const lineItemIndex = cart.items.findIndex(item => 
          item.variant_id.toString() === this.checkboxUpsellVariantId
        );
        if (lineItemIndex !== -1) {
          const line = lineItemIndex + 1;
          if (updateCart) {
            // Use direct cart change to update quantity, then force UI update
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                line: line,
                quantity: 0
              })
            }).then(response => {
              if (response.ok) {
                console.log('Upsell removed successfully');
                // Force full cart update to refresh all sections including checkout button
                this.onCartUpdateCheckboxUpsell();
              }
              return response.json();
            });
          } else {
            // Direct removal without full cart update
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                line: line,
                quantity: 0
              })
            }).then(response => {
              if (response.ok) {
                console.log('Upsell removed successfully');
                // Trigger a cart update event
                document.dispatchEvent(new CustomEvent('cart:changed'));
              }
              return response.json();
            });
          }
        } else {
          console.log('Cart upsell product not found in cart.');
        }
      })
      .catch(error => {
        console.error('Error fetching cart for cart upsell removal:', error);
      });
  }

  getSectionsToRenderCheckboxUpsell() {
    const sections = [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer .drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '#cart-icon-bubble',
      }
    ];

    // Also include main cart footer if it exists (for regular cart page)
    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter) {
      sections.push({
        id: 'main-cart-footer',
        section: mainCartFooter.dataset.id,
        selector: '#main-cart-footer .js-contents',
      });
    }

    return sections;
  }

  onCartUpdateCheckboxUpsell() {
    this.getSectionsToRenderCheckboxUpsell().forEach(section => {
      fetch(`${routes.cart_url}?section_id=${section.section}`)
        .then(response => response.text())
        .then(html => {
          const container = document.querySelector(section.selector);
          if (!container) {
            console.error(`Container not found for selector: ${section.selector}`);
            return;
          }
          
          if (section.id === 'CartDrawer') {
            // For cart drawer, update the entire inner content
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            const drawerInner = doc.querySelector('.drawer__inner');
  
            if (drawerInner) {
              container.innerHTML = drawerInner.innerHTML;
              console.log('Cart drawer updated with new totals');
            } else {
              console.error('.drawer__inner not found in fetched HTML for CartDrawer');
            }
          }
          else if (section.id === 'main-cart-footer') {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            const jsContents = doc.querySelector('.js-contents');
  
            if (jsContents) {
              container.innerHTML = jsContents.innerHTML;
            } else {
              console.error(`.js-contents not found in fetched HTML for section: ${section.id}`);
            }
          } 
          else if (section.id === 'cart-icon-bubble') {
            container.innerHTML = html;
          }
        })
        .catch(error => console.error(`Error updating section ${section.id}:`, error));
    });
  }
}

// Enhanced initialization
document.addEventListener('DOMContentLoaded', function() {
  const cartItemsInstance = document.querySelector('cart-items');
  const cartDrawerItemsInstance = document.querySelector('cart-drawer-items');
  let activeCartInstance = cartDrawerItemsInstance || cartItemsInstance; 
  
  if (activeCartInstance) {
    new CartCheckboxUpsell(activeCartInstance);
  } else {
    console.log("Neither cart-items nor cart-drawer-items found in the DOM.");
  }
});

// Enhanced product form integration
document.addEventListener('DOMContentLoaded', function() {
  // Listen for successful product additions
  const productForms = document.querySelectorAll('product-form');
  
  productForms.forEach(form => {
    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton) {
      submitButton.addEventListener('click', function() {
        // Set a flag that a product is being added
        window.cartUpsell.productBeingAdded = true;
        
        // Clear the flag after a delay
        setTimeout(() => {
          window.cartUpsell.productBeingAdded = false;
          
          // Trigger cart update check
          if (window.cartUpsell.initialized) {
            document.dispatchEvent(new CustomEvent('cart:updated'));
          }
        }, 1000);
      });
    }
  });
});

// Utility functions for theme settings integration
window.cartUpsell.enable = function() {
  window.cartUpsell.isEnabled = true;
};

window.cartUpsell.disable = function() {
  window.cartUpsell.isEnabled = false;
};

window.cartUpsell.setAutoAdd = function(enabled) {
  window.cartUpsell.autoAdd = enabled;
};

window.cartUpsell.setVariantId = function(variantId) {
  window.cartUpsell.variantId = variantId;
};